import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { StatusBadge } from '@/components/common/StatusBadge';
import { agentMeAPI, aepAPI, smartComplianceAPI, onboardingAPI } from '@/lib/api';
import { Cake } from 'lucide-react';

// Phase 27.14 — Timezone-safe birthday check. Parse the stored
// YYYY-MM-DD string as month/day only so a UTC-anchored Date object
// never trips the LOCAL calendar comparison (see AgentHeader for the
// same helper — kept inline to avoid a util file for two callers).
const isBirthdayToday = (dob) => {
  if (!dob || typeof dob !== 'string') return false;
  const m = dob.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return false;
  const now = new Date();
  return m[2] === String(now.getMonth() + 1).padStart(2, '0')
      && m[3] === String(now.getDate()).padStart(2, '0');
};
import { formatDate } from '@/lib/utils';
import { useAnnouncements } from '@/hooks/dashboard/useAnnouncements';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { OnboardingChecklist } from '@/components/common/OnboardingChecklist';
import {
  FileText, Ticket, ClipboardList, ArrowRight, Clock, Briefcase,
  CheckCircle, XCircle, User, Activity, Bell, Megaphone, ShieldCheck,
} from 'lucide-react';
import { DashboardShell } from './DashboardShell';
import { MetricCard } from './MetricCard';
import { ActionCenter } from './ActionCenter';
import { AgentContractingHealth } from './AgentContractingHealth';
import { AgentSnapshot } from './AgentSnapshot';
import RecentPayoutsWidget from './RecentPayoutsWidget';
import { KnowledgeHighlightsWidget } from './KnowledgeHighlightsWidget';
import { TopBirthdayBanner } from '@/components/dashboard/TopBirthdayBanner';
import { FollowupQueueWidget } from '@/components/crm/FollowupQueueWidget';
import { SmartComplianceTracker } from '@/components/common/SmartComplianceTracker';

const statusIcon = (status) => {
  switch (status) {
    case 'Approved': return <CheckCircle className="text-emerald-500" size={16} />;
    case 'Declined':
    case 'Terminated': return <XCircle className="text-red-500" size={16} />;
    default: return <Clock className="text-amber-500" size={16} />;
  }
};

export const AgentDashboard = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [agentProfile, setAgentProfile] = useState(null);
  const { announcements } = useAnnouncements();
  const [aepBadgeShow, setAepBadgeShow] = useState(false);
  const [compliancePending, setCompliancePending] = useState(0);
  const [onboardingPending, setOnboardingPending] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  // Same-page hash navigation (e.g. Action Center → #onboarding-checklist).
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const t = window.setTimeout(() => {
      const el = document.querySelector(`[data-testid="${id}"]`) || document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => window.clearTimeout(t);
  }, [location.hash, loading]);

  const load = async () => {
    try {
      const profileRes = await agentMeAPI.getProfile();
      setAgentProfile(profileRes.data);
      try {
        const badgeRes = await aepAPI.badgeCheck();
        setAepBadgeShow(!!badgeRes.data?.should_show);
      } catch (e) { /* non-critical */ }
      // Phase 27.12 — feed the discreet "Compliance Reminders" card on
      // the Required Actions band. Count is Pending + Expired items only;
      // Complete items don't need action.
      try {
        const npn = profileRes.data?.agent?.npn;
        if (npn) {
          const complRes = await smartComplianceAPI.get(npn);
          const pending = (complRes.data?.compliance_items || []).filter(
            (c) => c.status === 'Missing' || c.status === 'Expired',
          ).length;
          setCompliancePending(pending);
        }
      } catch (e) { /* non-critical */ }
      // Onboarding steps still owed by the agent — feeds the Action Center
      // (the detailed OnboardingChecklist section remains below).
      try {
        const chk = await onboardingAPI.checklist();
        const d = chk.data || {};
        setOnboardingPending(d.dismissed ? 0 : Math.max(0, (d.total || 0) - (d.completed || 0)));
      } catch (e) { /* non-critical */ }
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const profile = agentProfile;
  const counts = profile?.counts || {};
  const agent = profile?.agent || {};
  const recentAppts = (profile?.appointments || []).slice(0, 5);
  const recentReqs = (profile?.requests || []).slice(0, 5);
  const notifications = profile?.notifications || [];
  const activities = (profile?.activity || []).slice(0, 10);

  return (
    <DashboardShell
      title="Agent Dashboard"
      description="Manage your contracting activity, certifications, and account tasks."
    >
      {isBirthdayToday(agent.date_of_birth) && (
        <div
          className="mb-6 flex items-center gap-3 rounded-lg border border-pink-200 bg-gradient-to-r from-pink-50 via-amber-50 to-white px-4 py-3 shadow-sm"
          data-testid="birthday-banner"
        >
          <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center shrink-0">
            <Cake size={20} className="text-pink-600" />
          </div>
          <div className="min-w-0">
            <p className="font-heading font-semibold text-pink-800">
              Happy Birthday{agent.first_name ? `, ${agent.first_name}` : ''}!
            </p>
            <p className="text-xs text-slate-500">The whole KR team wishes you a fantastic day.</p>
          </div>
        </div>
      )}

      {/* Upcoming client birthdays (agents don't have downline agents,
          but they do have clients whose birthdays are worth surfacing). */}
      <TopBirthdayBanner days={14} />

      {/* 1 · ACTION CENTER — the single "what I need to do" block */}
      <ActionCenter
        scope="agent"
        extras={{
          complianceCount: compliancePending,
          onboardingCount: onboardingPending,
        }}
        parentLoading={loading}
      />

      {/* Follow-up tasks — destination for Action Center "Overdue follow-ups".
          Carries ?followup_window=overdue|soon to pre-select the tab. */}
      <div className="mb-8">
        <FollowupQueueWidget />
      </div>

      {/* Compliance tracker — destination for Action Center "Complete your
          compliance" (#compliance-tracker). Only rendered when something is
          actually pending so compliant agents don't see an empty block. */}
      {compliancePending > 0 && agent.npn && (
        <div className="mb-8" data-testid="compliance-tracker">
          <SmartComplianceTracker npn={agent.npn} />
        </div>
      )}

      {/* Announcements (informational) */}
      {announcements.length > 0 && (
        <Card className="mb-8 animate-slideIn stagger-2 border-l-4 border-l-blue-500" data-testid="dashboard-announcements">
          <CardHeader className="flex flex-row items-center justify-between py-3">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Megaphone size={16} className="text-blue-500" />
              Announcements
            </CardTitle>
            <Link to="/announcements"><Button variant="ghost" size="sm" className="text-xs">View All <ArrowRight size={12} className="ml-1" /></Button></Link>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            {announcements.map((ann) => (
              <div key={ann.announcement_id} className="p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                <p className="text-sm font-medium text-slate-900">{ann.title}</p>
                <p className="text-xs text-slate-600 line-clamp-2 mt-0.5">{ann.message}</p>
                <p className="text-[10px] text-slate-400 mt-1">{formatDate(ann.publish_date)}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* 2 · KEY KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard icon={User} value={agent.status || 'Active'} label="My Status" color="bg-slate-900" href="/profile" loading={loading} />
        <MetricCard icon={CheckCircle} value={counts.active_contracts} label="Active Contracts" color="bg-emerald-600" href="/my-contracts" loading={loading} />
        <MetricCard icon={Clock} value={counts.pending_requests} label="Pending Requests" color="bg-amber-500" href="/profile" loading={loading} />
        <MetricCard icon={ClipboardList} value={counts.open_tasks} label="Open Tasks" color="bg-sky-600" href="/tickets" loading={loading} />
      </div>

      <AgentSnapshot profile={profile} parentLoading={loading} />

      {/* 3 · OPERATIONAL / RECENT ACTIVITY */}
      <AgentContractingHealth
        requests={profile?.requests || []}
        appointments={profile?.appointments || []}
        parentLoading={loading}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Recent Contracts Snapshot */}
        <Card className="animate-slideIn stagger-2" data-testid="recent-contracts">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Briefcase size={18} className="text-slate-400" />
              My Contracts
            </CardTitle>
            <Link to="/my-contracts"><Button variant="outline" size="sm">View All</Button></Link>
          </CardHeader>
          <CardContent>
            {recentAppts.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Briefcase className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p className="font-medium text-sm">No contracts yet</p>
                <p className="text-xs mt-1">Submit a request to get appointed with carriers.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentAppts.map((appt) => (
                  <div key={appt.appointment_id} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors">
                    <div>
                      <p className="font-medium text-sm text-slate-900">{appt.carrier_name || 'Unknown'}</p>
                      <p className="text-xs text-slate-500">{appt.level_name || '-'} {appt.writing_number && `| WN: ${appt.writing_number}`}</p>
                    </div>
                    <StatusBadge status={appt.appointment_status} />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Requests Snapshot */}
        <Card className="animate-slideIn stagger-2" data-testid="recent-requests">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <FileText size={18} className="text-slate-400" />
              My Requests
            </CardTitle>
            <Link to="/profile"><Button variant="outline" size="sm">View All</Button></Link>
          </CardHeader>
          <CardContent>
            {recentReqs.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p className="font-medium text-sm">No requests yet</p>
                <p className="text-xs mt-1">Your contract requests will appear here.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentReqs.map((req) => (
                  <div key={req.request_id} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors">
                    <div>
                      <p className="font-medium text-sm text-slate-900">{req.carrier_name || 'Unknown'}</p>
                      <p className="text-xs text-slate-500">{req.level_name || '-'} | {formatDate(req.created_at)}</p>
                    </div>
                    <StatusBadge status={req.current_status} />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Notifications / Alerts */}
        <Card className="animate-slideIn stagger-3" data-testid="notifications-card">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Bell size={18} className="text-slate-400" />
              Notifications
              {notifications.length > 0 && <Badge className="bg-red-500 text-white text-xs">{notifications.length}</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {notifications.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No new notifications</p>
              </div>
            ) : (
              <div className="space-y-2">
                {notifications.slice(0, 5).map((notif) => (
                  <div key={notif.notification_id} className="p-3 rounded-lg bg-sky-50 border border-sky-100">
                    <p className="text-sm font-medium text-sky-900">{notif.title}</p>
                    <p className="text-xs text-sky-700 mt-1">{notif.message}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="animate-slideIn stagger-3" data-testid="activity-card">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Activity size={18} className="text-slate-400" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activities.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <Activity className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No recent activity</p>
              </div>
            ) : (
              <div className="space-y-3">
                {activities.slice(0, 6).map((act, i) => (
                  <div key={act.workflow_id || i} className="flex items-start gap-3">
                    <div className="mt-0.5">{statusIcon(act.status)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-slate-800 truncate">{act.carrier_name}</p>
                        <span className="text-xs text-slate-400 ml-2 shrink-0">{formatDate(act.changed_at)}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{act.status?.replace(/_/g, ' ')}{act.comment ? ` - ${act.comment}` : ''}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Secondary / conditional information */}
      {agent?.npn && (
        <RecentPayoutsWidget
          recipientType="agent"
          recipientId={agent.user_id || agent.npn}
          profileHref={`/agents/${agent.npn}?tab=payment`}
          limit={5}
        />
      )}

      {/* AEP 2027 PREP Badge — only shown when agent has active Medicare contracts */}
      {aepBadgeShow && agent.npn && (
        <Link
          to={`/agents/${agent.npn}?tab=aep-certifications`}
          className="block mb-8"
          data-testid="aep-2027-badge"
        >
          <Card className="border-l-4 border-l-sky-500 bg-gradient-to-r from-sky-50 to-white hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-sky-100 flex items-center justify-center shrink-0">
                  <ShieldCheck size={24} className="text-sky-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-heading font-semibold text-slate-900">AEP 2027 PREP</p>
                    <Badge className="bg-sky-100 text-sky-700 text-xs">Track your carrier certifications</Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Get ahead of the Annual Enrollment Period — mark each carrier as you finish certification.
                  </p>
                </div>
                <ArrowRight size={18} className="text-sky-600 shrink-0" />
              </div>
            </CardContent>
          </Card>
        </Link>
      )}

      {/* Onboarding Checklist — detailed section (Action Center links here) */}
      <OnboardingChecklist />

      {/* Knowledge highlights (last 3 active resources) */}
      <div className="mb-8">
        <KnowledgeHighlightsWidget />
      </div>

      {/* 4 · QUICK ACCESS */}
      <Card className="animate-slideIn stagger-1" data-testid="quick-actions">
        <CardHeader>
          <CardTitle className="text-lg font-heading">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Link to="/my-contracts">
              <Button variant="outline" className="w-full justify-start h-auto py-4">
                <Briefcase size={20} className="mr-3 text-emerald-600" />
                <div className="text-left">
                  <p className="font-medium">View My Contracts</p>
                  <p className="text-xs text-slate-500">See carrier appointments</p>
                </div>
              </Button>
            </Link>
            <Link to="/profile">
              <Button variant="outline" className="w-full justify-start h-auto py-4">
                <User size={20} className="mr-3 text-sky-600" />
                <div className="text-left">
                  <p className="font-medium">Update Profile</p>
                  <p className="text-xs text-slate-500">View your information</p>
                </div>
              </Button>
            </Link>
            <Link to="/tickets">
              <Button variant="outline" className="w-full justify-start h-auto py-4">
                <Ticket size={20} className="mr-3 text-amber-600" />
                <div className="text-left">
                  <p className="font-medium">Contact Support</p>
                  <p className="text-xs text-slate-500">Create a ticket</p>
                </div>
              </Button>
            </Link>
            <Link to="/profile">
              <Button variant="outline" className="w-full justify-start h-auto py-4">
                <FileText size={20} className="mr-3 text-violet-600" />
                <div className="text-left">
                  <p className="font-medium">My Requests</p>
                  <p className="text-xs text-slate-500">Track request status</p>
                </div>
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </DashboardShell>
  );
};

export default AgentDashboard;
